import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import { ContentContainer } from "../../pages";
import RejectImportModal from "../reject/RejectImportModal";

import acceptImports from "./acceptImportsMutation";

import { AdvancedReportPage } from "pstat-anywhere/components/pages/reports";
import useQueryVariables from "pstat-anywhere/components/pages/reports/hooks/useQueryVariables";
import { withLabelContext } from "pstat-anywhere/context_providers/LabelContext";
import { environment } from "pstat-anywhere/relay";
import titleCase from "pstat-anywhere/utils/titleCase";
import { getRootUrl } from "pstat-anywhere/utils/urls";
import AutocompleteTextInput, {
  INPUT_FOCUSED
} from "pstat-design-system/inputs/AutocompleteTextInput";
import { createSuccessMessage } from "pstat-design-system/message/manageMessages";
import { Button } from "pstat-design-system/shared/Button";
import { Text } from "pstat-design-system/text/paragraph";
import Tooltip from "pstat-design-system/Tooltip";



export const RESULTS_PER_PAGE = 20;

const ActionPanel = withLabelContext(
  ({ selectedPolicies, handleClearSelection, labels, refresh }) => {
    const [
      showAcceptanceConfirmation,
      setShowAcceptanceConfirmation
    ] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    let documentLabel = labels.documentLabel;
    const multiplePoliciesSelected = selectedPolicies.length > 1;
    if (multiplePoliciesSelected) {
      documentLabel = labels.documentLabelPlural;
    }
    const handleAccept = event => {
      setLoading(true);
      let variables = {
        importPks: selectedPolicies
      };
      acceptImports(environment, variables, (response, errors) => {
        setLoading(false);
        setShowAcceptanceConfirmation(false);
        if (errors) {
          throw new Error("error accepting policies");
        }
        if (response.acceptImports.ok) {
          handleClearSelection();
          createSuccessMessage(
            t(
              "implementation.documentsRequiringAcceptance.acceptSuccessMessage",
              {
                numberAccepted: selectedPolicies.length,
                documentLabel: documentLabel.toLowerCase()
              }
            )
          );
          refresh();
        } else {
          throw new Error(response.acceptImports.error.statusCode);
        }
      });
    };
    return (
      <Fragment>
        <Fragment>
          {" "}
          {showAcceptanceConfirmation ? (
            <Fragment>
              <Text color="nav.100">
                {t(
                  "implementation.documentsRequiringAcceptance.acceptConfirmationMessage",
                  {
                    documentLabelPlural: labels.documentLabelPlural.toLowerCase()
                  }
                )}
              </Text>
              <Button
                buttonStyle="primary"
                onClick={handleAccept}
                ml={2}
                processing={loading}
              >
                {t(
                  "implementation.documentsRequiringAcceptance.continueButton"
                )}
              </Button>
            </Fragment>
          ) : (
            <Fragment>
              {multiplePoliciesSelected ? (
                <Fragment>
                  <span
                    data-tip
                    data-for="reject-import-tooltip"
                    data-event="mouseenter focus"
                    data-event-off="mouseleave blur"
                    tabIndex="0"
                  >
                    <Button buttonStyle="tertiary" ml={2} disabled={true}>
                      {t(
                        "implementation.documentsRequiringAcceptance.rejectResultsButton",
                        { documentLabel: titleCase(documentLabel) }
                      )}
                    </Button>
                    <Tooltip name="reject-import-tooltip">
                      {t(
                        "implementation.documentsRequiringAcceptance.rejectMultipleTooltip",
                        {
                          documentLabelPlural: labels.documentLabelPlural.toLowerCase()
                        }
                      )}
                    </Tooltip>
                  </span>
                </Fragment>
              ) : (
                <Button
                  buttonStyle="tertiary"
                  onClick={() => setShowRejectModal(true)}
                  ml={2}
                >
                  {t(
                    "implementation.documentsRequiringAcceptance.rejectResultsButton",
                    { documentLabel: titleCase(documentLabel) }
                  )}
                </Button>
              )}
              <RejectImportModal
                isOpen={showRejectModal}
                onClose={() => setShowRejectModal(false)}
                importPk={selectedPolicies[0]}
                reloadPage={refresh}
              />
              <Button
                buttonStyle="primary"
                onClick={() => setShowAcceptanceConfirmation(true)}
                ml={2}
              >
                {t(
                  "implementation.documentsRequiringAcceptance.acceptAllSelectedResultsButton",
                  {
                    documentLabelPlural: documentLabel.toLowerCase(),
                    numberSelected: selectedPolicies.length
                  }
                )}
              </Button>
            </Fragment>
          )}
        </Fragment>
      </Fragment>
    );
  }
);

const getSelectedSuggestionsFromQuery = (query, suggestions) => {
  const selectedCategories = query.categories;
  if (!selectedCategories) {
    return [];
  }
  return suggestions.filter(
    suggestion => query.categories.indexOf(suggestion.pk) > -1
  );
};

const CategoryFilter = props => {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState(props.categories);
  const [selectedSuggestions, setSelectedSuggestions] = useState(
    getSelectedSuggestionsFromQuery(props.location.query, props.categories)
  );
  const handleSuggestionFetchRequested = ({ value, reason }) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    const suggestions =
      inputLength === 0 || reason === INPUT_FOCUSED
        ? props.categories
        : props.categories.filter(
            category =>
              getSuggestionValue(category).toLowerCase().indexOf(value) > -1
          );
    setSuggestions(suggestions);
  };
  const getSuggestionValue = suggestion => suggestion.name;
  const handleSelectedSuggetsionsChanged = suggestions => {
    const queryParams = { ...props.location.query };
    queryParams.categories = suggestions.map(suggestion => suggestion.pk);

    const to = {
      pathname: `${getRootUrl()}/implementation/accept`,
      query: queryParams
    };
    props.router.push(to);
    setSelectedSuggestions(suggestions);
  };
  return (
    <AutocompleteTextInput
      id="category_filter_input"
      suggestions={suggestions}
      selectedSuggestions={selectedSuggestions}
      onSuggestionSelected={handleSelectedSuggetsionsChanged}
      onSuggestionDeselected={handleSelectedSuggetsionsChanged}
      onSuggestionsFetchRequested={handleSuggestionFetchRequested}
      getSuggestionValue={getSuggestionValue}
      selectMultiple={true}
      shouldKeepSuggestionsOnSelect={() => true}
      inputIcon={["far", "search"]}
      placeholder={t(
        "implementation.documentsRequiringAcceptance.categoryFilterPlaceholder",
        { categoryLabel: props.labels.categoryLabel.toLowerCase() }
      )}
      containerProps={{ width: "100%", mb: 2 }}
      {...props}
    />
  );
};

function DocumentsRequiringAcceptancePage({
  documentsToAccept,
  route,
  router,
  location,
  labels,
  retry
}) {
  const { t } = useTranslation();
  const { variables } = useQueryVariables({ location });
  return (
    <ContentContainer>
      <AdvancedReportPage
        variables={variables}
        data={documentsToAccept?.documentsToAccept}
        route={route}
        router={router}
        location={location}
        tabs={[
          {
            id: "implementation_accept_report",
            textKey: "implementation.documentsRequiringAcceptance.tab"
          }
        ]}
        headerText={t("implementation.documentsRequiringAcceptance.title", {
          documentLabelPlural: titleCase(labels.documentLabelPlural)
        })}
        noContentMessage={t(
          "implementation.documentsRequiringAcceptance.noResults",
          {
            documentLabelPlural: labels.documentLabelPlural.toLowerCase()
          }
        )}
        showActionButtonPanel={true}
        showTabsPanel={true}
        tableType={"documentsRequiringAcceptance"}
        frozenColumnTable={true}
        alwaysShowActionPanel={true}
        renderActionPanel={(
          selectedPolicies,
          { handleClearSelection, handleSelectAll }
        ) => (
          <Fragment>
            {selectedPolicies.length > 0 && (
              <ActionPanel
                selectedPolicies={selectedPolicies}
                refresh={retry}
                handleClearSelection={handleClearSelection}
                handleSelectAll={handleSelectAll}
              />
            )}
          </Fragment>
        )}
        renderFilterPanel={() => (
          <Fragment>
            {documentsToAccept?.categories && (
              <CategoryFilter
                categories={documentsToAccept?.categories}
                router={router}
                location={location}
                labels={labels}
              />
            )}
          </Fragment>
        )}
        rowKey={({ rowData }) => rowData.document.pk}
      />
    </ContentContainer>
  );
}

export default withLabelContext(DocumentsRequiringAcceptancePage);
